import { ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { track } from "../../lib/analytics";
import { useGuest } from "../shell/GuestShell";

/**
 * "Viaje a la medida" entry points. The request form is for people who did not find their trip, so
 * it is offered where that happens — Explora (band), an empty search, the end of a region, a trip
 * without dates, Inicio — and opens with what they already told us (see `applyPrefill`).
 */
export interface CustomTripPrefill {
  /** Places for the "Destino o destinos" tokens. */
  destinations?: string[];
  /** A line for "¿Hay algo más que debamos saber?". */
  note?: string;
  /** "Aún no lo sé, quiero recomendaciones". */
  recommend?: boolean;
}

export function customTripPath({ destinations = [], note, recommend }: CustomTripPrefill = {}): string {
  const params = new URLSearchParams();
  for (const destination of destinations) if (destination.trim()) params.append("destino", destination.trim());
  if (note?.trim()) params.set("nota", note.trim());
  if (recommend) params.set("recomendar", "1");
  const query = params.toString();
  return `/viajes/a-la-medida${query ? `?${query}` : ""}`;
}

/** Opens the form with the prefill (guests are asked to create an account first, then land on it). */
export function useOpenCustomTrip(placement: string, prefill: CustomTripPrefill) {
  const navigate = useNavigate();
  const { guest, askForAccount } = useGuest();
  return () => {
    track("personalized_trip_card_clicked", { placement, authenticated: !guest });
    const path = customTripPath(prefill);
    if (guest) askForAccount("diseñar tu viaje a la medida", path);
    else navigate(path);
  };
}

const FACTS = ["Propuesta personalizada", "Sin costo ni compromiso", "Un especialista te contacta en 24 a 48 h hábiles"];

/** Full-width band (Explora, between the regions and the trips). */
export function CustomTripBand({ placement, prefill = {} }: { placement: string; prefill?: CustomTripPrefill }) {
  const open = useOpenCustomTrip(placement, prefill);
  return (
    <section className="custom-band" aria-labelledby="custom-band-title">
      <div className="custom-band-copy">
        <span className="hero-eyebrow"><Sparkles size={12} aria-hidden="true" /> Viaje a la medida</span>
        <h2 id="custom-band-title">¿No encuentras tu viaje? Lo diseñamos contigo</h2>
        <p>Cuéntanos qué tienes en mente. No necesitas tener todo decidido, nosotros te ayudamos a darle forma.</p>
        <ul>{FACTS.map((fact) => <li key={fact}>{fact}</li>)}</ul>
      </div>
      <button type="button" className="primary-button solid custom-band-cta" onClick={open}>Diseñar mi viaje <ArrowRight size={15} aria-hidden="true" /></button>
    </section>
  );
}

/** Compact card for the places where a search, a region or a trip leaves the customer wanting more. */
export function CustomTripInline({ placement, title, text, cta = "Diseñar mi viaje", prefill = {} }: {
  placement: string; title: string; text: string; cta?: string; prefill?: CustomTripPrefill;
}) {
  const open = useOpenCustomTrip(placement, prefill);
  return (
    <div className="custom-inline">
      <span className="benefit-icon"><Sparkles size={17} aria-hidden="true" /></span>
      <div><strong>{title}</strong><span>{text}</span></div>
      <button type="button" className="secondary-button small" onClick={open}>{cta} <ArrowRight size={14} aria-hidden="true" /></button>
    </div>
  );
}
